import React, { useEffect, useRef, useState } from 'react'

import { type DialogContentProps } from '@radix-ui/react-dialog'

import { CredenzaContent } from '@/components/ui/credenza'
import { DialogTitle } from '@/components/ui/dialog'

type DynamicCredenzaContentProps = React.ComponentPropsWithoutRef<typeof CredenzaContent> & {
  children: React.ReactNode
  className?: string
  open?: boolean
  selectedScreen?: string
} & Partial<DialogContentProps>

const DynamicCredenzaContent = ({
  children,
  className = '',
  open,
  selectedScreen,
  ...props
}: DynamicCredenzaContentProps) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const [contentHeight, setContentHeight] = useState('auto')
  const contentKey = React.useId()
  const calculateTimeoutRef = useRef<NodeJS.Timeout>()

  const calculateHeight = () => {
    if (!contentRef.current || !open) return

    if (calculateTimeoutRef.current) {
      clearTimeout(calculateTimeoutRef.current)
    }

    const scheduleCalculation = (delay: number) => {
      calculateTimeoutRef.current = setTimeout(() => {
        requestAnimationFrame(() => {
          if (!contentRef.current) return

          const viewportHeight = window.innerHeight
          const content = contentRef.current
          const totalHeight = content.scrollHeight
          const maxHeight = viewportHeight * 0.9
          const finalHeight = Math.max(totalHeight, maxHeight)

          setContentHeight(`${finalHeight}px`)
        })
      }, delay)
    }

    scheduleCalculation(0)
    scheduleCalculation(100)
    scheduleCalculation(300)
    scheduleCalculation(500)
  }

  useEffect(() => {
    calculateHeight()
    return () => {
      if (calculateTimeoutRef.current) {
        clearTimeout(calculateTimeoutRef.current)
      }
    }
  }, [children, contentKey])

  useEffect(() => {
    if (open || selectedScreen) {
      calculateHeight()
    }
  }, [open])

  useEffect(() => {
    if (open && selectedScreen) {
      calculateHeight()

      const recalculationTimes = [50, 100, 150, 200, 300, 400, 500]

      const timeouts = recalculationTimes.map((time) => setTimeout(() => calculateHeight(), time))

      const longTermTimeouts = [750, 1000].map((time) => setTimeout(() => calculateHeight(), time))

      return () => {
        timeouts.forEach((timeout) => clearTimeout(timeout))
        longTermTimeouts.forEach((timeout) => clearTimeout(timeout))
        if (calculateTimeoutRef.current) {
          clearTimeout(calculateTimeoutRef.current)
        }
      }
    }
  }, [selectedScreen])

  useEffect(() => {
    if (!open || !contentRef.current) return

    const mutationObserver = new MutationObserver(() => {
      calculateHeight()
    })

    mutationObserver.observe(contentRef.current, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    })

    const resizeObserver = new ResizeObserver(() => {
      calculateHeight()
    })

    resizeObserver.observe(contentRef.current)

    window.addEventListener('resize', calculateHeight)

    return () => {
      mutationObserver.disconnect()
      resizeObserver.disconnect()
      window.removeEventListener('resize', calculateHeight)
      if (calculateTimeoutRef.current) {
        clearTimeout(calculateTimeoutRef.current)
      }
    }
  }, [open])

  return (
    <CredenzaContent
      ref={contentRef}
      className={`scrollbar-none mx-auto w-full overflow-y-auto rounded-sm border-none ring-[1px] md:ring-[0.5px] [@media(max-resolution:1.9dppx)]:ring-[1px] ring-white-10 text-primary shadow-2xl bg-dark-background-24 ${className}`}
      style={{
        maxHeight: contentHeight,
        height: 'auto',
        minHeight: '250px',
        transition: 'max-height 150ms ease-in-out',
      }}
      {...props}
    >
      <DialogTitle className='sr-only'>Optimex</DialogTitle>
      {children}
    </CredenzaContent>
  )
}

export default DynamicCredenzaContent
